import {
  updatePassword,
  confirmSignIn,
  fetchAuthSession,
} from "aws-amplify/auth";
import { useState, useEffect } from "react";
import PasswordValidator from "password-validator";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { PasswordCheckComponent } from "./PasswordCheckComponent";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { PasswordInput } from "@/components/ui/password-input";
import { useForm } from "react-hook-form";
import { getUserPermissions } from "./GetUserPermissions";
import { useDispatch } from "react-redux";
import { setEmail, setPermissions } from "@/state/store/slice/userSlice";

const UpdatePassword = () => {
  const dispatch = useDispatch();
  const [showDialog, setShowDialog] = useState(false);
  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    return "";
  };
  // First-sign-in flow (NEW_PASSWORD_REQUIRED) flag set during sign-in
  const [isFirstSignIn, setIsFirstSignIn] = useState(
    sessionStorage.getItem("firstSignIn") === "true",
  );

  // Define a single schema; require current_password only when not first sign-in
  const formSchema = z
    .object({
      current_password: z.string().optional(),
      new_password: z.string(),
      confirm_new_password: z.string(),
    })
    .superRefine((args, ctx) => {
      // Clear previous errors
      form.clearErrors();

      if (args.confirm_new_password !== args.new_password) {
        ctx.addIssue({
          code: "custom",
          path: ["confirm_new_password"],
          message: "Password does not match",
        });
      }
      if (args.new_password !== args.confirm_new_password) {
        ctx.addIssue({
          code: "custom",
          path: ["new_password"],
          message: "Password does not match",
        });
      }
      if (
        !isFirstSignIn &&
        (!args.current_password || args.current_password.length < 1)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["current_password"],
          message: "Please enter old password",
        });
      }
    });

  //Set default values in from
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_new_password: "",
    },
    mode: "onChange",
  });

  const navigate = useNavigate();

  const [isLengthValid, setIsLengthValid] = useState(false);
  const [isUpperCase, setIsUpperCase] = useState(false);
  const [isLowerCase, setIsLowerCase] = useState(false);
  const [hasDigits, setHasDigits] = useState(false);
  const [hasSymbols, setHasSymbols] = useState(false);
  const [hasSpaces, setHasSpaces] = useState(false);

  const isValid = form.formState.isValid;
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);

  const backLink = isFirstSignIn || !isSignedIn ? "/sign-in" : "/";
  const backText =
    isFirstSignIn || !isSignedIn ? "Back to Sign In" : "Back to Home";

  // Password validation schema
  const passwordSchema = new PasswordValidator()
    .is()
    .min(8) // Minimum length 8
    .is()
    .max(20) // Maximum length 20
    .has()
    .uppercase() // Must have uppercase letters
    .has()
    .lowercase() // Must have lowercase letters
    .has()
    .digits() // Must have digits
    .has()
    .symbols() // Must have symbols
    .has()
    .not()
    .spaces(); // Should not have spaces

  //Validate password validates according to password schema
  const getPasswordValidation = (password: string) => {
    const failedRules = passwordSchema.validate(password, {
      list: true,
    }) as string[];

    const lengthValid =
      !failedRules.includes("min") && !failedRules.includes("max");
    const upperCaseValid = !failedRules.includes("uppercase");
    const lowerCaseValid = !failedRules.includes("lowercase");
    const digitsValid = !failedRules.includes("digits");
    const symbolsValid = !failedRules.includes("symbols");
    const spacesValid = !failedRules.includes("spaces");

    setIsLengthValid(lengthValid);
    setIsUpperCase(upperCaseValid);
    setIsLowerCase(lowerCaseValid);
    setHasDigits(digitsValid);
    setHasSymbols(symbolsValid);
    setHasSpaces(spacesValid);
    setIsPasswordValid(
      lengthValid &&
        upperCaseValid &&
        lowerCaseValid &&
        digitsValid &&
        symbolsValid &&
        spacesValid,
    );
  };

  //Call aws amplify updatepassword api
  const onUpdatePassword = (values: z.infer<typeof formSchema>) => {
    if (isFirstSignIn) {
      // Complete the first sign-in challenge (NEW_PASSWORD_REQUIRED)
      confirmSignIn({ challengeResponse: values.new_password })
        .then(async () => {
          // User stays signed in on success
          sessionStorage.removeItem("firstSignIn");

          // Get user permissions after successful password change
          try {
            const { email, permissions } = await getUserPermissions();
            dispatch(setEmail(email));
            dispatch(setPermissions(permissions));
            localStorage.setItem("email", email);
          } catch (error) {
            console.error("Failed to get user permissions:", error);
          }

          toast.success("Password set successfully");
          navigate("/");
        })
        .catch((error: unknown) => {
          const msg = getErrorMessage(error) || "Failed to set new password";
          toast.error(msg);
        });
      return;
    }

    // Regular password change for signed-in user (requires old password)
    updatePassword({
      oldPassword: values.current_password as string,
      newPassword: values.new_password,
    })
      .then(() => {
        toast.success("Password updated successfully");
        navigate("/");
      })
      .catch((error: unknown) => {
        const message = getErrorMessage(error);
        if (message === "User needs to be authenticated to call this API.") {
          toast.error("Please sign in to update your password");
        } else if (message === "Incorrect username or password.") {
          toast.error("Please check if the current password is correct");
          form.setValue("current_password", "");
          form.setValue("new_password", "");
          form.setValue("confirm_new_password", "");

          // Reset password validation states
          setIsLengthValid(false);
          setIsUpperCase(false);
          setIsLowerCase(false);
          setHasDigits(false);
          setHasSymbols(false);
          setHasSpaces(false);
          setIsPasswordValid(false);
        } else if (
          message === "Attempt limit exceeded, please try after some time."
        ) {
          toast.error(message);
        } else {
          toast.error(message || "Password update failed");
        }
      });
  };

  //Check if user is signed in on mount
  useEffect(() => {
    fetchAuthSession()
      .then((session) => {
        const signedIn = session.tokens !== undefined;
        setIsSignedIn(signedIn);

        // If user is already signed in, clear the firstSignIn flag
        if (signedIn) {
          sessionStorage.removeItem("firstSignIn");
          setIsFirstSignIn(false);
        }
      })
      .catch(() => setIsSignedIn(false));
  }, []);

  return (
    <div className="flex flex-col w-full">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onUpdatePassword)}
          className="space-y-8 max-w-full mx-4 sm:mx-20 md:mx-24 lg:mx-80 xl:mx-96 2xl:mx-[30rem] py-20"
        >
          <div className="bg-white shadow-2xl rounded-lg w-full p-10">
            <fieldset className="p-5 space-y-4">
              <h1 className="text-2xl font-bold">Change password</h1>
              {!isFirstSignIn && (
                <FormField
                  control={form.control}
                  name="current_password"
                  render={({ field }) => (
                    <FormItem className="text-xl">
                      <FormLabel className="text-xl">
                        Current Password
                      </FormLabel>
                      <FormControl>
                        <PasswordInput
                          className="text-xl h-12"
                          placeholder="Current Password"
                          {...field}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="new_password"
                render={({ field }) => (
                  <FormItem className="text-xl">
                    <FormLabel className="text-xl">New Password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        className="text-xl h-12"
                        placeholder="New Password"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          getPasswordValidation(e.currentTarget.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <PasswordCheckComponent
                isLengthValid={isLengthValid}
                isLowerCase={isLowerCase}
                hasDigits={hasDigits}
                hasSymbols={hasSymbols}
                hasSpaces={hasSpaces}
                isUpperCase={isUpperCase}
              />
              <FormField
                control={form.control}
                name="confirm_new_password"
                render={({ field }) => (
                  <FormItem className="text-xl">
                    <FormLabel className="text-xl">
                      Confirm New Password
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        className="text-xl h-12"
                        placeholder="Confirm New Password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full shadow-xl bg-red-700 hover:bg-red-800 hover:cursor-pointer text-white font-bold py-3 px-4 rounded-lg mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!isValid || !isPasswordValid}
              >
                Update Password
              </Button>
              <p className="text-left mt-2">
                <NavLink to={backLink} className="text-red-500 hover:underline">
                  {backText}
                </NavLink>
              </p>
            </fieldset>
          </div>
        </form>
      </Form>

      <Dialog
        open={showDialog}
        onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            navigate("/sign-in");
          }
        }}
      >
        <DialogContent>
          <div className="flex flex-col">
            <div>
              <b>Your password has been updated successfully.</b>
              <p>
                We're gradually introducing our new UI to ensure the best
                experience for everyone. Stay tuned!
              </p>
            </div>
            <div
              className="flex justify-end"
              onClick={() =>
                (window.location.href = `https://${import.meta.env.VITE_DOMAIN_NAME}`)
              }
            >
              <Button>Go to Revanista</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { UpdatePassword };
